import type { HyperlocalQuery } from '@vibesites/schema';
import { useProperty, useTemplateContext } from '@vibesites/runtime/react';

/**
 * The hyperlocal generator stores "no value" as the literal strings
 * "false", "null", "none", or empty — none of which are valid copy or
 * image URLs, but all are truthy in JS. Treat them as missing.
 */
export function isHyperlocalSentinel(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = String(value).trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  return (
    lower === 'false' ||
    lower === 'true' ||
    lower === 'null' ||
    lower === 'none' ||
    lower === 'undefined'
  );
}

export function pickHyperlocalImage(value: string | null | undefined): string | null {
  if (isHyperlocalSentinel(value)) return null;
  return String(value).trim();
}

/**
 * Current hyperlocal query for this page.
 *
 * Prefers `templateContext.hyperlocal` (the matching item on an expanded
 * `/featuring/{page_item_url}` page). When the template itself is open in
 * the editor — no item selected — fall back to the first query on the
 * property so title, subtitle, copy, and featured image still resolve.
 */
export function useCurrentHyperlocal(): HyperlocalQuery | undefined {
  const ctx = useTemplateContext();
  const property = useProperty();
  if (ctx?.hyperlocal) return ctx.hyperlocal;

  const queries = property?.hyperlocal_queries;
  if (Array.isArray(queries) && queries[0]) return queries[0];
  return undefined;
}
